import { PlayCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { InventoryAuditModal } from '../components/InventoryAuditModal';
import { StartInventoryAuditModal } from '../components/StartInventoryAuditModal';
import { inventoryAuditsApi } from '../inventoryAuditApi';
import type { InventoryAuditListItem } from '../inventoryAuditTypes';
import { INVENTORY_AUDIT_STATUS } from '../inventoryAuditTypes';
import { storageFacilitiesApi } from '../inventoryApi';

/**
 * The inventory-audit register — physical stock takes, and what each one found.
 *
 * <b>The anchor is the NUMBER.</b> An audit has no name, two counts raised on one day over one
 * store are told apart by nothing else, and the number is what a filed count sheet quotes. The
 * source stacks the window under it; here the window is its OWN column, because a stacked value
 * in an index table has no header, no width, no sort and no settings entry — and because the
 * list view renders these same columns, so a hidden second value would make the two views
 * silently disagree.
 *
 * <b>AUDIT BY is deliberately not a column, and it is the omission a reader will notice.</b> The
 * row carries a `personnelId`; turning thirty of those into names is a two-hop through the
 * identity store — a personnel read each, then one resolve — which is a request per row on every
 * page load, and it would need `personnel.read` for every reader of this register. The profile
 * resolves it for the one record on screen, which is the call the issuance register and
 * `RealProperty`'s custodian already take. So this list cannot be searched or sorted by who
 * counted, and that is a property of the identity split rather than something to fix here.
 */
export function InventoryAuditsIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [starting, setStarting] = useState<InventoryAuditListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    audit: InventoryAuditListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    An audit counts STORES, so one has to exist before the form can be submitted. Seeded TRUE and
    left true on a failed feed: flashing "register a store first" on every load of a working
    module is worse than a banner arriving a moment late, and a request that did not come back is
    not evidence that there are no stores.
  */
  const [hasFacility, setHasFacility] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    storageFacilitiesApi
      .options(controller.signal)
      .then((facilities) => {
        if (controller.signal.aborted) return;
        setHasFacility(facilities.length > 0);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchAudits = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      inventoryAuditsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          facility: query.facets.facility?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (audit: InventoryAuditListItem): MenuItem[] => [
      /*
        ABSENT rather than disabled on an audit that is not a draft — a count that has already
        started, finished or been voided has no start left to press, so the entry does not apply
        to that record at all. That is the hide-don't-disable rule read correctly: it is not
        withheld because of who is reading.
      */
      ...(audit.status === INVENTORY_AUDIT_STATUS.draft
        ? [
            {
              key: `start-${audit.id}`,
              label: 'Start count',
              icon: <PlayCircle className="h-4 w-4" aria-hidden="true" />,
              permission: 'inventory-audits.start',
              onClick: () => setStarting(audit),
            } satisfies MenuItem,
          ]
        : []),
      {
        key: `restore-${audit.id}`,
        label: 'Restore',
        permission: 'inventory-audits.delete',
        disabledReason: audit.isActive ? 'This audit is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', audit }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await inventoryAuditsApi.deactivate(confirming.audit.id);
      else await inventoryAuditsApi.activate(confirming.audit.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<InventoryAuditListItem>
        module="inventory-audits"
        breadcrumb={['Logistics', 'Inventory', 'Inventory Audits']}
        columns={COLUMNS}
        /*
          Filed by STAGE rather than the default status axis: the question a reader opens this
          register with is what is still being counted, not whether a row is deleted. They
          repoint it from the rail's cog if they file differently, and that choice is remembered
          per module.
        */
        folderNavFacet="stage"
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Stage' },

          // GATED: this module's implied facility read is scoped to create, so a read-only
          // reader genuinely lacks it and the gate hides something real.
          { key: 'facility', label: 'Store', permission: 'storage-facilities.read' },
        ]}
        prerequisites={[
          {
            label: 'a storage facility',
            met: hasFacility,
            to: '/inventory/settings/storage-facilities',
            permission: 'storage-facilities.read',
          },
        ]}
        /*
          Every type this module's workflow WRITES, children included — a module whose list names
          only the parent hides every write to every child, and the failure is silent in the worst
          way: the row IS in `AuditLogs` and only the module's own history is blind.
        */
        auditEntityTypes={[
          'InventoryAudit',
          'InventoryAuditFacility',
          'InventoryAuditLine',
          'InventoryAuditLineFacility',
        ]}
        rowKey={(audit) => audit.id}
        fetch={fetchAudits}
        reloadKey={reloadKey}
        searchPlaceholder="Search audit number or notes"
        emptyMessage="No stock take has been raised yet."
        onCreate={() => setCreating(true)}
        createPermission="inventory-audits.create"
        onRowOpen={(audit) => navigate(`/inventory/inventory-audits/${audit.id}`)}
        openPermission="inventory-audits.read"
        rowActions={rowActions}
        onDelete={(audit) => setConfirming({ kind: 'deactivate', audit })}
        deletePermission="inventory-audits.delete"
        deleteLabel={() => 'Delete'}
        /*
          Disabled for STATE, and the reason names the other action. The server refuses it too —
          the rule lives there — but meeting it as a 409 after a password prompt is a worse way to
          learn that deleting is not cancelling.
        */
        deleteDisabledReason={(audit) =>
          !audit.isActive
            ? 'This audit is already deleted.'
            : audit.status === INVENTORY_AUDIT_STATUS.inProgress
              ? 'This audit is still being counted. Cancel it with a reason instead — deleting is for a sheet raised against the wrong window.'
              : null
        }
        otherInfo={(audit) => audit}
      />

      {creating ? (
        <InventoryAuditModal
          open
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {starting ? (
        <StartInventoryAuditModal
          open
          auditId={starting.id}
          auditNumber={starting.auditNumber}
          auditStartDate={starting.auditStartDate}
          facilities={starting.facilities}
          onClose={() => setStarting(null)}
          onStarted={(audit) => {
            setStarting(null);
            navigate(`/inventory/inventory-audits/${audit.id}`);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete inventory audit' : 'Restore inventory audit'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`inventory-audit-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Audit ${confirming.audit.auditNumber} stops appearing in the register.`
                  : `Audit ${confirming.audit.auditNumber} appears in the register again.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  <b>Deleting is not cancelling.</b> It corrects the register — a sheet raised
                  against the wrong window or the wrong stores. If the count was abandoned, cancel
                  it with a reason so the record says what happened.
                </p>
              ) : null}

              <p>
                No stock moves either way. An audit records what was counted; it never changes a
                quantity.
              </p>

              <p>Nothing is erased. A deleted audit can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<InventoryAuditListItem>[] = [
  {
    key: 'auditNumber',
    header: 'Audit',
    width: 190,
    sortable: true,
    render: (audit) => <TextCell value={audit.auditNumber} mono />,
  },
  {
    key: 'auditStartDate',
    header: 'Window',
    width: 190,
    sortable: true,
    summary: true,
    render: (audit) => (
      <TextCell
        value={`${formatDateOnly(audit.auditStartDate)} – ${formatDateOnly(audit.auditEndDate)}`}
        mono
      />
    ),
  },
  {
    key: 'facilities',
    header: 'Stores',
    width: 220,
    summary: true,
    /*
      Joined on ONE line, which is the anchor exception read for an ordinary column: an audit's
      scope is one fact whose value happens to be a set, exactly as a training record's
      competencies are. There is no alternative shape — the count is per row, and a table cannot
      grow a column per store. It is consequently not sortable: a set has no order a header could
      promise.
    */
    render: (audit) => (
      <TextCell value={audit.facilities.map((f) => f.storageFacilityName).join(', ') || null} />
    ),
  },
  {
    key: 'snapshotAsOf',
    header: 'Inventory as of',
    width: 175,
    sortable: true,
    /*
      An EM DASH until the audit starts, and it is the honest answer rather than a placeholder:
      the baseline is captured at Start, so before then there is genuinely no instant to show. A
      creation date here would claim every line's snapshot was true at a moment nothing was read.
    */
    render: (audit) => (
      <TextCell value={audit.snapshotAsOf == null ? null : formatDateTime(audit.snapshotAsOf)} mono />
    ),
  },
  {
    key: 'counted',
    header: 'Counted',
    width: 110,
    /*
      ONE value, where the source stacks a fraction over a progress bar. A two-line cell in an
      index table has no header, no width entry and no sort, it breaks the row rhythm the eye
      scans along, and the list view renders these same columns — so the bar would be visible in
      one view and absent in the other. The fraction carries everything the bar did.
    */
    render: (audit) => <TextCell value={`${audit.countedItems} / ${audit.totalItems}`} mono numeric />,
  },
  {
    key: 'netVariance',
    header: 'Net variance',
    width: 130,
    /*
      An EM DASH until something has been counted, because a net of 0 is true for two different
      reasons — nothing counted, and counted in agreement — and only one of them is a clean sheet.
      `numeric` mutes a genuine zero, which is what tells the two apart at a glance.
    */
    render: (audit) => (
      <TextCell
        value={audit.countedItems === 0 ? null : formatSigned(audit.netVariance)}
        mono
        numeric
      />
    ),
  },
  {
    key: 'itemsWithVariance',
    header: 'Flagged',
    width: 100,
    render: (audit) => (
      <TextCell
        value={audit.countedItems === 0 ? null : String(audit.itemsWithVariance)}
        mono
        numeric
      />
    ),
  },
  {
    key: 'stage',
    /** The record's own lifecycle — the shell sits it beside the status pill. */
    stage: true,
    header: 'Stage',
    width: 130,
    sortable: true,
    /*
      TWO state columns on one row, deliberately — the call the disciplinary panel and the
      issuance register already take. This is the audit's own lifecycle as plain text; the column
      below is the ROW's live/deleted marker, which is a different fact and the one a reader scans
      for. The label is the SERVER's, so no surface coins a second word for one state.
    */
    render: (audit) => <StatusText tone="inactive">{audit.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (audit) => (
      <StatusText tone={audit.isActive ? 'active' : 'inactive'}>
        {audit.isActive ? 'Active' : 'Deleted'}
      </StatusText>
    ),
  },
];

/** A net variance reads with its sign, so a surplus and a shortfall are told apart at a glance. */
function formatSigned(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}
